import apiService from '../apiService';
import { cleanSourceText } from './contentFormatter';
import {
  detectFindingSourceType,
  getSafeSourceUrl,
  isEurLexCelex,
  isMeaningful,
  type FindingSourceFields,
} from './findingSourceType';

export interface FindingSourceLinkFields extends FindingSourceFields {
  finding_id: string;
  source_reference?: string;
}

export interface FindingViewerFields extends FindingSourceLinkFields {
  source_snippet?: string;
  // BAR-533 version pin — set on graph article findings.
  valid_from?: string;
  valid_to?: string;
  version_status?: string;
}

/** Consolidated-family CELEX: sector-3 legislation (32013R0575) maps to the
 *  sector-0 consolidated family (02013R0575). Mirrors the backend resolver
 *  (eurlex_version_resolver.consolidated_base). */
export function consolidatedBaseCelex(celex: string): string {
  return celex.length >= 2 && celex[0] === '3' ? `0${celex.slice(1)}` : celex;
}

export interface EurLexExternalOpts {
  articleRef?: string;   // adds an #art_NN fragment server-side
  /** Point-in-time to resolve at — pass `findingAsOf(finding)`, i.e. set ONLY
   *  for a historical (superseded/repealed) finding. Omitted ⇒ the CURRENT
   *  consolidated version, which is what a live finding should link to. */
  asOf?: string;
}

/** External "Open on EUR-Lex" link for a finding.
 *
 *  Points at the backend resolver (`/eurlex/{celex}/link`), which 302s to the
 *  EUR-Lex page for the CONSOLIDATED version — the user still lands on
 *  eur-lex.europa.eu.
 *
 *  Why not build the CELEX here? Because only the server can tell which
 *  consolidated version exists:
 *    * The bare base act (`32013R0575`) serves the ORIGINAL Official Journal
 *      text — the 2013 CRR as adopted, not any consolidation. That was the
 *      long-standing "right article, wrong version" bug (verified 2026-08-17).
 *    * There is no dateless "latest" URL: `02013R0575` alone 404s. Only a
 *      dated CELEX (`02013R0575-20250629`) reaches consolidated text.
 *    * A finding's `valid_from` is NOT reliably a published consolidation date
 *      — the graph also carries ingestion timestamps (`2025-11-24T22:03:38`)
 *      and base-act dates (`2013-06-26`). Building `0{celex}-{valid_from}` in
 *      the browser therefore produces DEAD EUR-Lex pages for some findings,
 *      which is worse than showing the wrong version.
 *
 *  The backend reuses the same BAR-533 resolver + candidate fallback chain as
 *  the in-BARK iframe render, so the embedded view and this link can never
 *  disagree — and it degrades to the base act rather than a 404.
 *
 *  With no `asOf` the resolver returns the CURRENT consolidated version; pass
 *  `findingAsOf(finding)` so only a historical finding pins to its own dated
 *  consolidation. Same rule the iframe uses. */
export function buildEurLexUrl(
  celex: string | undefined,
  opts?: EurLexExternalOpts,
): string | null {
  if (!isEurLexCelex(celex)) return null;
  const backendUrl = apiService.getWorkflowOrchestratorUrl();
  const params = new URLSearchParams();
  if (opts?.articleRef) params.set('article_ref', opts.articleRef);
  if (opts?.asOf) params.set('as_of', opts.asOf);
  const qs = params.toString();
  return `${backendUrl}/eurlex/${encodeURIComponent(celex)}/link${qs ? `?${qs}` : ''}`;
}

/** `sandbox` for an iframe that loads the orchestrator's EUR-Lex preview
 *  (`buildEurLexViewerUrl`) by URL. The page has the orchestrator's origin, so
 *  `allow-same-origin` keeps only that origin, never the app's.
 *
 *  `allow-popups` + `allow-popups-to-escape-sandbox` (BAR-1556): a link in the
 *  preview ("Open the full document on EUR-Lex") opens a normal tab. Without
 *  them the click did nothing, and a sandboxed tab would break EUR-Lex itself.
 *  This is safe for this frame and not for ArtifactIframe's (user content):
 *  the preview is public legislation and an orchestrator template, and its
 *  Content-Security-Policy runs only the template's own script and embeds no
 *  frame, so nothing else in it can open a window. Never
 *  `allow-top-navigation`: the preview must not navigate the app. */
export const EURLEX_PREVIEW_SANDBOX =
  'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox';

/** The same for a prefetched preview rendered through `srcDoc`. srcDoc
 *  inherits THIS app's origin, so it must never get `allow-same-origin`: the
 *  frame would run in the app's origin (session, storage). */
export const EURLEX_PREVIEW_SRCDOC_SANDBOX =
  'allow-scripts allow-popups allow-popups-to-escape-sandbox';

/** The in-BARK article viewer URL. `asOf` (ISO date) renders the consolidated
 *  version in force on that date (BAR-533). */
export function buildEurLexViewerUrl(celex: string, articleRef: string, asOf?: string): string {
  const backendUrl = apiService.getWorkflowOrchestratorUrl();
  const base = `${backendUrl}/eurlex/${celex}/article/${encodeURIComponent(articleRef)}`;
  return asOf ? `${base}?as_of=${encodeURIComponent(asOf)}` : base;
}

const HISTORICAL_VERSION_STATUSES = new Set(['repealed', 'superseded']);

/** True only when the cited version is NO LONGER current law — the sole case
 *  that should pin the render/link to a dated historical consolidation.
 *
 *  Gate on the research-time `version_status`, NOT on the presence of
 *  `valid_to`: the graph emits `in_force_until` (a FUTURE `valid_to`) on
 *  versions that are in force TODAY but have a scheduled future amendment —
 *  pervasive in staged CRR3/CRD6/DORA texts. Those are still current and must
 *  not be pinned. For legacy findings with a `valid_to` but no status, fall
 *  back to a closed validity window (`valid_to` on/before today). */
export function isHistoricalVersion(
  finding: { valid_to?: string; version_status?: string },
): boolean {
  if (finding.version_status) {
    return HISTORICAL_VERSION_STATUSES.has(finding.version_status);
  }
  // No status → historical only if the half-open window has already closed.
  return finding.valid_to ? finding.valid_to <= new Date().toISOString().slice(0, 10) : false;
}

/** The point-in-time to render a finding's source at: the consolidation it was
 *  researched against, but ONLY when the finding cites a HISTORICAL
 *  (superseded/repealed) version. A current in-force finding — including one
 *  with a scheduled future amendment — renders today's text with no banner. */
export function findingAsOf(
  finding: { valid_from?: string; valid_to?: string; version_status?: string },
): string | undefined {
  return finding.valid_from && isHistoricalVersion(finding) ? finding.valid_from : undefined;
}

export function parsePageNumber(ref: string | undefined): number | null {
  if (!ref) return null;
  const match = ref.match(/\(p\.(\d+)\)/);
  return match ? parseInt(match[1], 10) : null;
}

export function isLikelyPdfDocumentId(value: string | undefined): value is string {
  if (!isMeaningful(value)) return false;
  const trimmed = value.trim();
  if (trimmed.includes('://')) return false;
  return /^[A-Za-z0-9._/-]+$/.test(trimmed);
}

export function buildPdfViewerUrl(docId: string, page: number | null): string | null {
  if (!isLikelyPdfDocumentId(docId)) return null;
  const backendUrl = apiService.getWorkflowOrchestratorUrl();
  const url = docId.includes('/')
    ? `${backendUrl}/pdf/view?doc_id=${encodeURIComponent(docId)}`
    : `${backendUrl}/pdf/${encodeURIComponent(docId)}/view`;
  const params = 'navpanes=0&view=FitH';
  return page ? `${url}#page=${page}&${params}` : `${url}#${params}`;
}

export function extractPdfDocIdFromFields(
  chunkId: string | undefined,
  sourceDocumentCelex: string | undefined,
): string | null {
  if (chunkId) {
    const match = chunkId.match(/^(.+?)_p\d+/);
    if (match) return match[1];
  }
  if (isLikelyPdfDocumentId(sourceDocumentCelex)) {
    return sourceDocumentCelex.trim();
  }
  return null;
}

export function extractPdfDocId(finding: FindingSourceLinkFields): string | null {
  return extractPdfDocIdFromFields(finding.chunk_id, finding.source_document_celex);
}

/**
 * The exact URL the finding's embedded "Source content" iframe loads —
 * the EUR-Lex article view (with the same `?highlight=` the detail panel
 * scrolls to) or the PDF viewer. Returns null for non-embeddable findings
 * (Q&A, concept, workspace-input, unknown).
 *
 * SINGLE SOURCE OF TRUTH for this URL: the finding detail panel renders
 * its iframe from this, and the background prefetch warms the browser
 * cache from this. They MUST be byte-identical — a prefetch of a
 * different URL warms nothing — so both call this one function.
 */
export function buildFindingSourceViewerUrl(finding: FindingViewerFields): string | null {
  const type = detectFindingSourceType(finding);
  if (type === 'eurlex') {
    if (!finding.source_document_celex || !finding.source_reference) return null;
    const base = buildEurLexViewerUrl(
      finding.source_document_celex, finding.source_reference, findingAsOf(finding),
    );
    const highlight = finding.source_snippet ? cleanSourceText(finding.source_snippet) : '';
    if (!highlight) return base;
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}highlight=${encodeURIComponent(highlight.substring(0, 200))}`;
  }
  if (type === 'pdf') {
    const docId = extractPdfDocId(finding);
    if (!docId) return null;
    const page = finding.page_number ?? parsePageNumber(finding.source_reference);
    return buildPdfViewerUrl(docId, page);
  }
  return null;
}

function sourceDocumentKey(value: string | undefined): string | null {
  return isMeaningful(value) ? value.trim().toLowerCase() : null;
}

function sameSourceDocument(
  sourceDocumentCelex: string | undefined,
  sourceDocumentName: string | undefined,
  candidate: FindingSourceLinkFields,
): boolean {
  const aCelex = sourceDocumentKey(sourceDocumentCelex);
  const bCelex = sourceDocumentKey(candidate.source_document_celex);
  if (aCelex && bCelex && aCelex === bCelex) return true;

  const aName = sourceDocumentKey(sourceDocumentName);
  const bName = sourceDocumentKey(candidate.source_document_name);
  return Boolean(aName && bName && aName === bName);
}

export function safeDocumentUrl(finding: FindingSourceFields): string | null {
  return (
    getSafeSourceUrl(finding.source_url) ||
    getSafeSourceUrl(finding.pdf_url) ||
    getSafeSourceUrl(finding.page_url)
  );
}

export function findPeerDocumentUrl(
  findingId: string,
  sourceDocumentCelex: string | undefined,
  sourceDocumentName: string | undefined,
  allFindings: Record<string, FindingSourceLinkFields>,
): string | null {
  for (const candidate of Object.values(allFindings)) {
    if (candidate.finding_id === findingId) continue;
    if (!sameSourceDocument(sourceDocumentCelex, sourceDocumentName, candidate)) continue;
    if (detectFindingSourceType(candidate) !== 'pdf') continue;

    const url = safeDocumentUrl(candidate);
    if (url) return url;
  }
  return null;
}
